import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Milk, Gift } from "lucide-react";

export default function Register() {
  const [params] = useSearchParams();
  const refFromUrl = params.get("ref") || "";
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
    referral_code: refFromUrl,
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    setLoading(true);
    const res = await register(form);
    setLoading(false);
    if (res.ok) navigate("/dashboard");
    else setError(res.error);
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-dd-page" data-testid="register-page">
      <div className="hidden md:block relative bg-dd-green-deep overflow-hidden">
        <img
          src="https://static.prod-images.emergentagent.com/jobs/e14b0732-6b72-4e6d-935c-0035d2b6e307/images/296bead7ce024d2338e605450c0c4253ef701f38b4c8025fd33afc2a0c9dd694.png"
          alt="Gold milk pot"
          className="w-full h-full object-cover opacity-75"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-dd-green-deep/85 via-dd-green-deep/60 to-[#0A1F15]/95" />
        <div className="absolute top-10 left-12 flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-dd-gold/15 border border-dd-gold flex items-center justify-center">
            <Milk className="w-5 h-5 text-dd-gold" strokeWidth={1.75} />
          </div>
          <div>
            <div className="font-serif italic text-2xl text-dd-cream">Divya Dhara</div>
            <div className="dd-label text-[0.58rem] text-dd-gold">A2 Gaumata Milk</div>
          </div>
        </div>
        <div className="absolute bottom-12 left-12 right-12 text-dd-cream">
          <div className="dd-label text-dd-gold mb-3 tracking-[0.3em]">JOIN 2,300+ FAMILIES</div>
          <h2 className="font-serif text-5xl leading-tight font-devanagari text-dd-gold-soft" data-testid="register-tagline">हर बूंद में अमृत</h2>
          <p className="mt-4 text-[#E8DEC7] text-lg">Fresh A2 Gir cow milk delivered in sealed packets every morning.</p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 md:p-12 bg-dd-page">
        <div className="w-full max-w-md">
          <div className="dd-label text-dd-gold mb-2">Create Account</div>
          <h1 className="font-serif text-4xl text-dd-cream">Welcome to the family</h1>
          <p className="mt-2 text-dd-cream-muted">Use a referral code and get ₹100 wallet credit.</p>

          <form onSubmit={submit} className="mt-7 space-y-4" data-testid="register-form">
            <div>
              <label className="dd-label text-dd-cream-muted block mb-2">Full Name</label>
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full bg-white/5 border border-dd-gold/30 text-dd-cream placeholder:text-dd-cream-muted/60 rounded-sm px-4 py-3 focus:border-dd-gold focus:ring-1 focus:ring-dd-gold outline-none"
                data-testid="register-name" />
            </div>
            <div>
              <label className="dd-label text-dd-cream-muted block mb-2">Email</label>
              <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full bg-white/5 border border-dd-gold/30 text-dd-cream placeholder:text-dd-cream-muted/60 rounded-sm px-4 py-3 focus:border-dd-gold focus:ring-1 focus:ring-dd-gold outline-none"
                data-testid="register-email" />
            </div>
            <div>
              <label className="dd-label text-dd-cream-muted block mb-2">Phone</label>
              <input required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="+91"
                className="w-full bg-white/5 border border-dd-gold/30 text-dd-cream placeholder:text-dd-cream-muted/60 rounded-sm px-4 py-3 focus:border-dd-gold focus:ring-1 focus:ring-dd-gold outline-none"
                data-testid="register-phone" />
            </div>
            <div>
              <label className="dd-label text-dd-cream-muted block mb-2">Password</label>
              <input required type="password" minLength={6} value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="w-full bg-white/5 border border-dd-gold/30 text-dd-cream placeholder:text-dd-cream-muted/60 rounded-sm px-4 py-3 focus:border-dd-gold focus:ring-1 focus:ring-dd-gold outline-none"
                data-testid="register-password" />
            </div>
            <div>
              <label className="dd-label text-dd-cream-muted block mb-2 flex items-center gap-1.5">
                <Gift className="w-3.5 h-3.5 text-dd-gold" /> Referral Code (optional)
              </label>
              <input
                value={form.referral_code}
                onChange={(e) => setForm({ ...form, referral_code: e.target.value.toUpperCase() })}
                placeholder="DIVYA-XXXXXX"
                className="w-full bg-white/5 border border-dd-gold/30 text-dd-cream placeholder:text-dd-cream-muted/60 rounded-sm px-4 py-3 focus:border-dd-gold focus:ring-1 focus:ring-dd-gold outline-none font-mono"
                data-testid="register-referral"
              />
              <p className="text-xs text-dd-gold/80 mt-1">Both you and your referrer get ₹100 credit.</p>
            </div>
            {error && <div className="text-sm text-[#F5A99A]" data-testid="register-error">{error}</div>}
            <button type="submit" disabled={loading}
              className="w-full bg-gradient-to-b from-dd-gold to-[#8F6E28] text-dd-ink-dark py-3 rounded-sm font-bold hover:from-dd-gold-light hover:to-dd-gold transition-colors disabled:opacity-60 tracking-wide"
              data-testid="register-submit">
              {loading ? "Creating…" : "Create Account"}
            </button>
          </form>

          <div className="mt-6 text-sm text-dd-cream-muted">
            Already have an account?{" "}
            <Link to="/login" className="text-dd-gold font-medium hover:underline">Sign in</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
